import { pure } from '@core/helpers/pure';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { TUI_CONFIRM, TuiRadioList } from '@taiga-ui/kit';
import { XAlertService } from '@core/services';
import { RequestService } from '@core/services/request/request.service';
import { TranslatePipe } from '@ngx-translate/core';
import { TuiDialogService, TuiButton } from '@taiga-ui/core';
import { OutkeepTranslateService } from '@core/services/outkeep-translate.service';
import {
  RequestFormInspectionsTranslationsPayload,
  RequestFormInspectionTranslations,
} from '@core/types';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, filter, switchMap } from 'rxjs';
import { Nullable } from '@core/helpers';
import { catchError } from 'rxjs/operators';

enum FormFields {
  InspectionResult = 'inspectionResult',
}

@Component({
  selector: 'x-request-form-inspection',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TuiRadioList,
    TranslatePipe,
    TuiButton,
  ],
  templateUrl: './request-form-inspection.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestFormInspectionComponent implements OnInit {
  $isInsideSummary = input<boolean>(false, {
    alias: 'isInsideSummary',
  });
  private readonly formBuilder = inject(FormBuilder);
  private readonly requestService = inject(RequestService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogService$ = inject(TuiDialogService);
  private readonly xAlertService = inject(XAlertService);
  private readonly outkeepTranslateService = inject(OutkeepTranslateService);

  translates: RequestFormInspectionsTranslationsPayload = {
    [RequestFormInspectionTranslations.DialogUpdateInspectionStatus]:
      'Вы уверены что хотите сохранить данные осмотра?',
    [RequestFormInspectionTranslations.SuccessUpdatingInspectionStatus]:
      'Данные осмотра успешно обновлены',
    [RequestFormInspectionTranslations.ErrorUpdatingInspectionStatus]:
      'Случилась ошибка при обновлении статуса осмотра',
    [RequestFormInspectionTranslations.DialogUpdateInspectionStatusYes]: 'Да',
    [RequestFormInspectionTranslations.DialogUpdateInspectionStatusNo]: 'Нет',
    [RequestFormInspectionTranslations.InspectionStatusNotChanged]:
      'Изначальный статус осмотра не отличается от нового',
  };

  readonly FormFields = FormFields;
  private initialInspectionResult: Nullable<boolean> = null;

  public readonly values = [
    { value: null, name: 'inspection_status_null' },
    { value: true, name: 'inspection_status_true' },
    { value: false, name: 'inspection_status_false' },
  ];

  public formGroup = this.formBuilder.group({
    [FormFields.InspectionResult]: new FormControl(this.values[0]),
  });

  ngOnInit(): void {
    this.outkeepTranslateService
      .translate([...Object.values(RequestFormInspectionTranslations)])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(translates => (this.translates = translates));

    const inspectionResult =
      this.requestService.$request()?.inspectionResult ?? null;

    this.initialInspectionResult = inspectionResult;

    this.formGroup.patchValue({
      [FormFields.InspectionResult]: this.values.find(
        v => v.value === inspectionResult,
      ),
    });
  }

  @pure
  get inspectionResultField() {
    return this.formGroup.controls[FormFields.InspectionResult];
  }

  public submit() {
    const updatedInspectionResul =
      this.inspectionResultField.value?.value ?? null;

    if (!this.formGroup.dirty) {
      return;
    }

    if (this.initialInspectionResult === updatedInspectionResul) {
      this.xAlertService.error(
        this.translates[
          RequestFormInspectionTranslations.InspectionStatusNotChanged
        ],
      );

      return;
    }

    this.dialogService$
      .open<boolean>(TUI_CONFIRM, {
        label:
          this.translates[
            RequestFormInspectionTranslations.DialogUpdateInspectionStatus
          ],
        size: 's',
        closable: false,
        data: {
          yes: this.translates[
            RequestFormInspectionTranslations.DialogUpdateInspectionStatusYes
          ],
          no: this.translates[
            RequestFormInspectionTranslations.DialogUpdateInspectionStatusNo
          ],
        },
      })
      .pipe(
        filter(Boolean),
        switchMap(() =>
          this.requestService.updateInspectionResult(
            this.initialInspectionResult,
            updatedInspectionResul,
          ),
        ),
        catchError(() => {
          this.xAlertService.error(
            this.translates[
              RequestFormInspectionTranslations.ErrorUpdatingInspectionStatus
            ],
          );
          this.formGroup.reset({
            [FormFields.InspectionResult]: this.values.find(
              v => v.value === this.initialInspectionResult,
            ),
          });

          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.xAlertService.success(
          this.translates[
            RequestFormInspectionTranslations.SuccessUpdatingInspectionStatus
          ],
        );
        const updatedInspectionResul = this.values.find(
          v => v.value === this.inspectionResultField.value!.value,
        );

        this.formGroup.reset({
          [FormFields.InspectionResult]: updatedInspectionResul,
        });
      });
  }

  public cancel() {
    this.formGroup.reset({ [FormFields.InspectionResult]: this.values[0] });
  }
}
